import React from 'react'
import { PageProps, graphql } from 'gatsby'
import { GatsbyImage, ImageDataLike, getImage, getSrc } from 'gatsby-plugin-image'
import { FaFacebookSquare, FaInstagramSquare, FaYoutube, FaLinkedin } from 'react-icons/fa'

import { Button, Container, Seo, WhatsAppButton } from 'components'
import { ArticleCard, ArticleHero, BlocksRenderer, CategoryList } from 'components/blog'
import { Layout } from 'components/layouts'
import { useAnalytics } from 'hooks'
import { toAbsoluteUrl } from 'services/file'

export const Head = ({ data }: PageProps<Queries.ArticleQuery>) => {
  const { article } = data
  if (!article) return null

  const image16x9 = getSrc(article.banner?.localFile as ImageDataLike)
  const image4x3 = getSrc(article.structuredBanner4x3?.localFile as ImageDataLike)
  const image1x1 = getSrc(article.structuredBanner1x1?.localFile as ImageDataLike)

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    image: [image16x9 && toAbsoluteUrl(image16x9), image4x3 && toAbsoluteUrl(image4x3), image1x1 && toAbsoluteUrl(image1x1)].filter(
      Boolean,
    ),
    datePublished: article.isoPublishedAt,
    dateModified: article.isoUpdatedAt,
    author: [{ '@type': 'Person', name: article.author?.name }],
  }

  return (
    <Seo
      seo={{
        structuredData,
        shareImageUrl: image16x9,
        metaTitle: article?.title,
        metaDescription: article?.description,
      }}
    />
  )
}

export const BlogArticle = ({ data }: PageProps<Queries.ArticleQuery>) => {
  const { article, related, globalData } = data
  if (!article) return null

  const { fireEvent } = useAnalytics()

  const articleBanner = getImage(article.banner?.localFile as ImageDataLike)
  const authorAvatarImage = getImage(article.author?.avatar?.localFile as ImageDataLike)

  const socialMedias = [
    {
      icon: <FaInstagramSquare aria-label='Ícone do Instagram' color='var(--color-secondary-600)' size='28' />,
      link: globalData?.linkInstagram,
      label: 'hubscontabilidade',
      analyticsTarget: 'linkInstagram',
    },
    {
      icon: <FaLinkedin aria-label='Ícone do Linkedin' color='var(--color-secondary-600)' size='28' />,
      link: globalData?.linkLinkedin,
      label: 'hubscontabilidade',
      analyticsTarget: 'linkLinkedin',
    },
    {
      icon: <FaFacebookSquare aria-label='Ícone do Facebook' color='var(--color-secondary-600)' size='28' />,
      link: globalData?.linkFacebook,
      label: 'hubscontabilidade',
      analyticsTarget: 'linkFacebook',
    },
    {
      icon: <FaYoutube aria-label='Ícone do Youtube' color='var(--color-secondary-600)' size='28' />,
      link: globalData?.linkYoutubeChannel,
      label: '@hubscontabilidade',
      analyticsTarget: 'linkYoutubeChannel',
    },
  ]

  // Disparo do evento de clique externo
  const fireAnalyticsEvent = (target: string) => {
    fireEvent('externalClick', {
      component: 'BlogArticlePage',
      target,
    })
  }

  return (
    <Layout>
      <ArticleHero title={article?.title || ''} />

      <Container className='pt-16'>
        <div className='grid grid-cols-3 gap-x-10 auto-cols-max'>
          <div className='flex flex-col w-full col-span-3 lg:col-span-2'>
            {articleBanner && (
              <GatsbyImage className='rounded-xl drop-shadow-xl' image={articleBanner} alt={`Ilustração do Artigo: ${article.title}`} />
            )}
            <span className='bg-primary-600 text-black text-xs font-medium me-2 px-2.5 py-0.5 rounded-full w-auto m-4 absolute drop-shadow-xl'>
              {article.category?.name}
            </span>

            <div className='pt-10'>
              <h2 className='font-semibold text-2xl'>{article.description}</h2>

              <BlocksRenderer
                className='pt-5 prose prose-sm prose-li:marker:font-bold prose-li:marker:text-primary-900 prose-hr:border-black'
                blocks={article.content || []}
              />

              <div className='rounded-xl drop-shadow-lg bg-white bg-opacity-50 lg:max-w-lg my-10'>
                <div className='py-5 px-3 flex gap-4 sm:gap-10 flex-row items-center'>
                  <div className='flex items-center justify-center drop-shadow-sm'>
                    <div className='rounded-full bg-gradient-to-r from-primary-800 to-primary-500 flex justify-center items-center p-2'>
                      {authorAvatarImage && (
                        <GatsbyImage
                          className='rounded-full'
                          image={authorAvatarImage}
                          alt={`Foto de perfil de autor: ${article?.author?.name}`}
                        />
                      )}
                    </div>
                  </div>

                  <div className='flex flex-col justify-center gap-1'>
                    <p className='text-sm sm:text-md font-normal'>Publicado por:</p>
                    <p className='text-xl sm:text-2xl font-semibold'>{article.author?.name}</p>
                    <p className='text-sm sm:text-md font-normal'>em {article.publishedAt}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className='flex flex-col w-full gap-10 justify-start col-span-3 lg:col-span-1 pt-10 lg:pt-0 border-t-4 border-white lg:border-t-0'>
            <CategoryList className='rounded-xl drop-shadow-xl bg-white bg-opacity-50 p-8' />

            <div className='rounded-xl drop-shadow-xl bg-white bg-opacity-50 p-8'>
              <p className='font-semibold text-xl mb-3'>Fale com a Hubs!</p>
              <p className='font-normal text-md'>
                Nossa equipe de atendimento ao cliente está disponível para tirar todas as suas dúvidas!
              </p>

              <WhatsAppButton onClick={() => fireAnalyticsEvent('linkWhatsapp')}>
                <Button className='w-full mt-8 font-light text-md'>Falar com um especialista</Button>
              </WhatsAppButton>
            </div>

            <div className='rounded-xl drop-shadow-xl bg-white bg-opacity-50 p-8'>
              <p className='font-semibold text-xl mb-5'>Siga a Hubs</p>
              <div className='flex flex-col gap-6'>
                {socialMedias.map((item) => (
                  <a
                    href={item.link || ''}
                    rel='noreferrer noopener'
                    target='_blank'
                    className='flex flex-row items-center gap-4 underline'
                    key={item.link}
                    onClick={() => fireAnalyticsEvent(item.analyticsTarget)}
                  >
                    {item.icon}
                    <span className='font-light'>{item.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <div className='py-10 lg:py-14 my-10 border-t-4 border-white'>
          <h2 className='font-semibold text-3xl'>Artigos relacionados</h2>

          <div className='mt-10 lg:place-items-start grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-8'>
            {(related.nodes || []).map((article: Queries.ArticleCardFragment) => (
              <ArticleCard article={article} key={article.id} />
            ))}
          </div>
        </div>
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query Article($slug: String!) {
    related: allStrapiArticle(limit: 3, sort: [{ createdAt: DESC }], filter: { slug: { ne: $slug } }) {
      nodes {
        ...ArticleCard
      }
    }

    article: strapiArticle(slug: { eq: $slug }) {
      id
      slug
      title
      description
      isoPublishedAt: publishedAt
      isoUpdatedAt: updatedAt
      publishedAt(formatString: "DD/MM/YYYY", locale: "pt-BR")
      category {
        slug
        name
      }
      author {
        avatar {
          localFile {
            childImageSharp {
              gatsbyImageData(aspectRatio: 1, width: 112)
            }
          }
        }
        name
      }
      content {
        __typename
        # ... on STRAPI__COMPONENT_SHARED_MEDIA {
        #   file {
        #     mime
        #     localFile {
        #       childImageSharp {
        #         gatsbyImageData
        #       }
        #     }
        #   }
        # }
        ... on STRAPI__COMPONENT_SHARED_RICH_TEXT {
          body {
            __typename
            data {
              id
              childMarkdownRemark {
                html
              }
            }
          }
        }
      }
      banner {
        name
        localFile {
          childImageSharp {
            gatsbyImageData(aspectRatio: 1.77777777778)
          }
        }
      }
      # For google rich cards
      structuredBanner4x3: banner {
        name
        localFile {
          childImageSharp {
            gatsbyImageData(aspectRatio: 1.33333333, layout: FIXED, placeholder: NONE, transformOptions: { cropFocus: CENTER }, height: 480)
          }
        }
      }
      structuredBanner1x1: banner {
        name
        localFile {
          childImageSharp {
            gatsbyImageData(aspectRatio: 1, layout: FIXED, placeholder: NONE, transformOptions: { cropFocus: CENTER }, height: 480)
          }
        }
      }
    }

    globalData: strapiGlobal {
      linkYoutubeChannel
      linkLinkedin
      linkFacebook
      linkInstagram
    }
  }
`

export default BlogArticle
